// CRM navigation data — the link tables, the CRM-specific visibility gate, and
// the two rail hooks the gateway layout uses.
//
// One home, two consumers: the object-driven sidebar providers
// (./providers/crmProviders.tsx) render these as panel rows, and AppShellLayout
// uses the rail hooks. CRM gates on its OWN permission map
// (`useCrmPermissions` → /crm/users/me/permissions, resolved through
// `resolveCrmNavPermission`), NOT the shared @clarix/rbac store — so its rows
// are pre-filtered here rather than by the engine's `visibleWhen`.

import { useMemo } from 'react';
import {
  Phone,
  AlertTriangle,
  BadgeDollarSign,
  Blocks,
  BarChart3,
  Building2,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Contact,
  Database,
  FileSearch,
  FileText,
  Handshake,
  KeyRound,
  Layers,
  LineChart,
  Package,
  RefreshCw,
  ShieldCheck,
  Target,
  Upload,
  UsersRound,
  Bell,
  Boxes,
  type LucideIcon,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { RailItem } from '@clarix/shared-types';
import { resolveCrmNavPermission } from '@clarix/rbac';
import { MASTER_GROUPS, type MasterGroupId } from '@clarix/services-master-config';
import { useActiveCrmObjectNames, useCustomCrmObjects } from '../../core/api/crm-objects.api';
import { useCrmPermissions } from '../../core/api/crm-permissions.api';

export interface CrmPanelLink {
  label: string;
  to: string;
  icon: LucideIcon;
  /** crm.* permission gate; row hidden once perms load if the user lacks it. */
  permKey?: string;
  /**
   * CRM object api_name backing this row. When set, the row is hidden if the
   * admin has DEACTIVATED that object (the active-objects gate is fail-open: an
   * unresolved set never hides anything).
   */
  objectApiName?: string;
  /**
   * Per-object view key for CUSTOM objects — the REAL `module.<api>.view` key the
   * backend seeds + resolves per object (was the unresolved `crm.o.<api>.read`
   * convention, which always read `undefined` and never gated). Refines the
   * generic `permKey` (`crm.customobject.read`) group gate: hide a specific
   * object only when the resolved map explicitly denies view (`=== false`). The
   * group `permKey` already fail-closes the whole custom-object set while the map
   * is unresolved, so this per-object check need only handle the explicit-deny case.
   */
  objectPermKey?: string;
  /**
   * Optional group heading this row sits under. Consecutive rows sharing a
   * `group` render as one labelled block; rows with no `group` form the panel's
   * leading unlabelled block. Used by the Operations panel to keep its Finance
   * destinations visually separate from Approvals / Product / Import.
   */
  group?: string;
}

export const CRM_PANEL_SECTIONS: Record<string, CrmPanelLink[]> = {
  // Operations panel — the former "Finance" rail, widened. Leads with the
  // operational destinations (Approvals, Product & Package, Import), then a
  // "Finance" group (Subscriptions, Renewals, Reports). Every destination lives
  // under /crm/operations/* so the URL names the rail it belongs to.
  //
  // The core CRM entities (Companies / Contacts / Leads / Deals / Tasks) are NOT
  // here any more — each owns a top-level rail icon, and admin-defined custom
  // objects are appended to the rail by `useCrmRailItems`.
  'crm-operations': [
    {
      label: 'Approvals',
      to: '/crm/operations/approvals',
      icon: ClipboardCheck,
      permKey: 'crm.approval.read',
    },
    {
      label: 'Product & Package',
      to: '/crm/operations/products',
      icon: Package,
      permKey: 'crm.product.read',
      objectApiName: 'product',
    },
    { label: 'Import', to: '/crm/operations/import', icon: Upload, permKey: 'crm.import.read' },
    {
      label: 'Subscriptions',
      to: '/crm/operations/subscriptions',
      icon: RefreshCw,
      permKey: 'crm.subscription.read',
      group: 'Finance',
    },
    {
      label: 'Renewals',
      to: '/crm/operations/renewals',
      icon: RefreshCw,
      permKey: 'crm.subscription.write',
      group: 'Finance',
    },
    {
      label: 'Reports',
      to: '/crm/operations/reports',
      icon: BarChart3,
      permKey: 'crm.report.read',
      group: 'Finance',
    },
  ],
  // Commission panel — source PANEL_ITEMS.commission.
  'crm-commission': [
    {
      label: 'Commission',
      to: '/crm/commission',
      icon: BadgeDollarSign,
      permKey: 'crm.commission.read',
    },
    {
      label: 'Incentive Structures',
      to: '/crm/settings/incentive-structures',
      icon: Coins,
      permKey: 'crm.commission.write',
    },
  ],
  // Reports panel — source PANEL_ITEMS.reports. No rail item points at it any
  // more (Reports is a row in the Operations panel's Finance group); kept so a
  // deep link to the analytics page still resolves a panel body.
  'crm-reports': [
    // Gated on the analytics key itself, matching the route guard — not the
    // broader `crm.report.read` it used to share.
    {
      label: 'Approval Analytics',
      to: '/crm/operations/reports/approval-analytics',
      icon: LineChart,
      permKey: 'crm.approvalanalytics.read',
    },
  ],
  // Master panel — NOT a static section. Source renders master-config
  // categories dynamically (PanelContent's MasterPanelContent -> useCategories()),
  // grouped by MASTER_GROUPS with a search box + `?group=&category=` deep links.
  // Now reproduced shell-side by `CrmMasterPanel` (special-cased in
  // renderSidebarPanel) via the shared `@clarix/services-master-config` package,
  // backed by a `/crm`-scoped client configured in main.tsx. So `crm-master` is
  // intentionally absent from this static map.
};

/**
 * Settings destinations — formerly the standalone `crm-settings` rail panel.
 * Settings no longer owns a rail icon: these rows now render as the LEADING
 * "Settings" group inside the Master panel (see `CrmMasterPanel`), so master
 * config and app configuration share one rail entry. Deliberately NOT part of
 * `CRM_PANEL_SECTIONS` — there is no `crm-settings` panelId to route to.
 */
export const CRM_SETTINGS_LINKS: readonly CrmPanelLink[] = [
  // Users now has its OWN top-level rail item (`crm-users`), so it no longer
  // appears as a row here — Team management stays.
  // Team management (named teams: name + description + head + members). Reuses the
  // user-directory gate (crm.user.read) — no dedicated crm.team.* key/seeder.
  {
    label: 'Team management',
    to: '/crm/settings/team',
    icon: UsersRound,
    permKey: 'crm.user.read',
  },
  {
    label: 'Roles & Permissions',
    to: '/crm/settings/roles-permissions',
    icon: KeyRound,
    permKey: 'crm.rbac.read',
  },
  // Objects & fields schema builder. It used to own the top-level "Config" rail,
  // whose panel listed every object as a row; that rail is retired and the object
  // list is now the PAGE's own tab switcher (`?tab=<api_name>`), with New object
  // and delete beside the tabs. So this is one row here, beside Roles &
  // Permissions — both are "administer the CRM itself" destinations.
  {
    label: 'CRM Configuration',
    to: '/crm/settings/crm-configuration',
    icon: Blocks,
    permKey: 'crm.objects.read',
  },
  // Seller identity + GST state + quote validity (quotation engine config).
  {
    label: 'Quotation',
    to: '/crm/settings/quotation',
    icon: FileText,
    permKey: 'crm.masterconfig.read',
  },
  // Notification catalogue + per-workspace channel toggles. The label names BOTH
  // channels the page toggles (in-app AND email), so the row says what it does.
  {
    label: 'Notifications & Emails',
    to: '/crm/settings/notifications',
    icon: Bell,
    permKey: 'crm.notificationconfig.read',
  },
  // Calling (VoIP) — the per-workspace telephony provider. Labelled "Calling"
  // rather than "VoIP Configuration": a salesperson recognises calling, not the
  // protocol. Admin-only, so the row simply is not there for anyone else.
  {
    label: 'Calling (VoIP)',
    to: '/crm/settings/voip',
    icon: Phone,
    permKey: 'crm.voipconfig.read',
  },
  // Compliance is NOT listed here — it is its own group in the same panel, see
  // CRM_COMPLIANCE_LINKS below.
  // TEMP-HIDDEN (this version): Auto-task Rules.
  // {
  //   label: 'Auto-task Rules',
  //   to: '/crm/settings/auto-task-rules',
  //   icon: Zap,
  //   permKey: 'crm.autotask.write',
  // },
  // Password Policy, IP Allowlist, and Geography settings were removed from the
  // CRM app. Password policy + IP allowlist are now platform-wide and live in
  // the shared workspace-admin surface (/workspace/security/*).
  // 'Approvals' is NOT here — it renders under the "Approval Configuration"
  // master group instead (see GROUP_EXTRA_LINKS).
];

/**
 * Compliance destinations — formerly the standalone `crm-compliance` rail panel.
 * Compliance no longer owns a rail icon: these rows render as the "Compliance"
 * group inside the Settings panel, directly after the Settings group, and every
 * page now lives under `/crm/settings/*` (the old `/crm/compliance/*` paths
 * redirect). Like `CRM_SETTINGS_LINKS`, deliberately NOT part of
 * `CRM_PANEL_SECTIONS` — there is no `crm-compliance` panelId to route to.
 *
 * Gated per row, so a data-protection admin without DSR access still sees Data
 * Protection. Keep in sync with the `crm-master` rail item's ANY-of permission.
 */
export const CRM_COMPLIANCE_LINKS: readonly CrmPanelLink[] = [
  { label: 'DSR Inbox', to: '/crm/settings/dsr-inbox', icon: FileSearch, permKey: 'crm.dsr.read' },
  {
    label: 'Data Protection',
    to: '/crm/settings/data-protection',
    icon: ShieldCheck,
    permKey: 'crm.dataprotection.read',
  },
  {
    label: 'Breach Incidents',
    to: '/crm/settings/breach-incidents',
    icon: AlertTriangle,
    permKey: 'crm.breach.read',
  },
  {
    label: 'Data Access Logs',
    to: '/crm/settings/data-access-logs',
    icon: Database,
    permKey: 'crm.audit.read',
  },
  {
    label: 'Consent Notices',
    to: '/crm/settings/consent-notice-templates',
    icon: FileText,
    permKey: 'crm.consentnotices.read',
  },
  // TEMP-HIDDEN (this version): SPDI Backfill.
  // { label: 'SPDI Backfill', to: '/crm/settings/spdi-backfill', icon: FileSpreadsheet, permKey: 'crm.audit.read' },
  //
  // Audit Log (/crm/settings/audit-log) and Recovery (/crm/settings/recovery)
  // are NOT rows here any more — both are occasional, admin-only destinations,
  // so they moved to the rail's 9-dot "More" popover, where a user who does
  // reach for them can PIN one onto the rail. They are declared as CRM
  // `moreItems` (apps/crm/src/rail.ts, mirrored in the crmRegister in apps.ts)
  // and gated there on the same `crm.audit.read` / `crm.recovery.read` keys.
  // Their routes are unchanged, so every existing deep link still resolves.
];

/**
 * Config links attached to a specific MASTER group, rendered above that group's
 * master-config categories in the panel. Lets an app-config destination live
 * beside the related master data — e.g. the Approvals matrix/TAT page sits under
 * "Approval Configuration" next to the approval_reason / approval_action
 * categories, rather than in the generic Settings group. Permission-gated per
 * row exactly like the Settings links.
 */
export const GROUP_EXTRA_LINKS: Partial<Record<MasterGroupId, readonly CrmPanelLink[]>> = {
  // Approval Matrix + Approval TAT merged into one tabbed "Approvals" section.
  // Gated on approval-ADMIN keys, not the CRM approvals inbox key.
  approval: [
    {
      label: 'Approvals',
      to: '/crm/settings/approvals',
      icon: Layers,
      permKey: 'crm.approvalconfig.read',
    },
  ],
};

export const CRM_PANEL_TITLES: Record<string, string> = {
  'crm-operations': 'Operations',
  'crm-commission': 'Commission',
  'crm-reports': 'Reports',
  'crm-users': 'Users',
};

/**
 * Is a single CRM nav row visible for this user? Shared by the panel body and
 * the rail-visibility hook so a row and its rail icon stay in lock-step.
 *  - Static permission gate: resolve the `crm.*` alias to real `module.*` key(s)
 *    and require ANY of them. FAIL-CLOSED — a permission-gated row stays hidden
 *    until the CRM map resolves, then shows only on an ANY-of key match. This
 *    matches the RBAC "fail closed" rule and the rail's fail-closed-while-pending
 *    behaviour. (An alias that resolves to NO keys is genuinely ungated and
 *    always shows — after the notifications fix there are no such rows.)
 *  - Per-object gate (custom objects): the static gate above already fail-closes
 *    the whole custom-object group, so this only ADDITIONALLY hides an object the
 *    resolved map explicitly denies view on (`module.<api>.view === false`).
 *  - Active-object gate: fail-open — an unresolved set hides nothing.
 */
export function crmRowVisible(
  link: Pick<CrmPanelLink, 'permKey' | 'objectApiName' | 'objectPermKey'>,
  crmPerms: Record<string, boolean> | null,
  activeObjects: Set<string> | null,
  permissionsLoaded: boolean,
): boolean {
  if (link.permKey) {
    const keys = resolveCrmNavPermission(link.permKey);
    if (keys.length > 0) {
      // Fail CLOSED: hide until the map has resolved, then require an ANY-of match.
      if (!permissionsLoaded || !crmPerms) return false;
      if (!keys.some((k) => crmPerms[k] === true)) return false;
    }
  }
  if (
    link.objectPermKey &&
    permissionsLoaded &&
    crmPerms &&
    crmPerms[link.objectPermKey] === false
  ) {
    return false;
  }
  if (link.objectApiName && activeObjects && !activeObjects.has(link.objectApiName)) return false;
  return true;
}

/**
 * Which CRM rail panels have at least one visible row for this user — so the
 * rail icon is hidden when its whole panel is empty (e.g. no Settings access →
 * no Settings icon). Returns `null` while CRM permissions are unresolved
 * (fail-open: show every rail icon). Pass `enabled = false` off the CRM app to
 * skip the CRM fetches entirely.
 */
export function useVisibleCrmPanelIds(enabled = true): Set<string> | null {
  const { map: crmPerms, loaded } = useCrmPermissions(enabled);
  const activeObjects = useActiveCrmObjectNames(enabled);

  return useMemo(() => {
    if (!enabled || !loaded || !crmPerms) return null; // fail-open
    const visible = new Set<string>();
    for (const [panelId, links] of Object.entries(CRM_PANEL_SECTIONS)) {
      const anyVisible = links.some((l) => crmRowVisible(l, crmPerms, activeObjects, loaded));
      if (anyVisible) visible.add(panelId);
    }
    // Master panel is data-driven (not in CRM_PANEL_SECTIONS) — gate its rail
    // icon on master-config view access (module.masterconfig.view), matching the
    // crm-master rail item's `crm.masterconfig.read` permission. It also hosts
    // the Settings group AND the per-group extra links (e.g. Approvals), so a
    // user who can reach ANY of those rows (but not master config) must still
    // get the Master icon.
    const canViewMasterConfig = resolveCrmNavPermission('crm.masterconfig.read').some(
      (k) => crmPerms[k] === true,
    );
    const extraLinks = MASTER_GROUPS.flatMap((g) => GROUP_EXTRA_LINKS[g.id] ?? []);
    const canViewAnyLink = [...CRM_SETTINGS_LINKS, ...CRM_COMPLIANCE_LINKS, ...extraLinks].some(
      (l) => crmRowVisible(l, crmPerms, activeObjects, loaded),
    );
    if (canViewMasterConfig || canViewAnyLink) {
      visible.add('crm-master');
    }
    // (There is no `crm-data` panel any more — CRM Configuration is a row in the
    // Settings panel above, so `canViewAnyLink` already covers its gate.)
    // Users panel is a fixed three-row list (All / Active / Deactivated) — gate
    // its rail icon on user-directory read access, matching the crm-users rail
    // item's `crm.user.read` permission.
    if (resolveCrmNavPermission('crm.user.read').some((k) => crmPerms[k] === true)) {
      visible.add('crm-users');
    }
    return visible;
  }, [enabled, loaded, crmPerms, activeObjects]);
}

/**
 * `api_name` backing each STATIC CRM entity rail item. The entities used to be
 * rows inside the "CRM" panel, where they were hidden once an admin DEACTIVATED
 * the object; now that each owns a rail icon, the rail has to apply that same
 * gate. Fail-OPEN, exactly like the panel rows: an unresolved object set hides
 * nothing. (Permissions are still handled by the rail's own `crm.*` gate.)
 */
export const CRM_ENTITY_RAIL_OBJECTS: Record<string, string> = {
  'crm-companies': 'company',
  'crm-contacts': 'contact',
  'crm-leads': 'lead',
  'crm-deals': 'deal',
  'crm-tasks': 'task',
};

/**
 * The CRM rail, resolved against live workspace data.
 *
 *  - Drops a static entity icon whose object the admin has deactivated.
 *  - Appends one icon per active CUSTOM (admin-defined) object, → /crm/o/:apiName,
 *    inserted directly after the last static entity icon so user-defined objects
 *    sit with the built-in ones rather than at the end of the rail. Each carries
 *    the object's own chosen Lucide icon. This is what makes "add an object →
 *    it appears in the rail" work with no code change.
 *
 * Custom-object items are gated HERE (CRM `module.*` keys via `crmRowVisible`)
 * and deliberately carry no `permission`, since the shell rail's own filter
 * checks the ecosystem `crm.*` namespace, which has no per-object key.
 *
 * Pass `enabled = false` off the CRM app to skip the CRM fetches entirely.
 */
export function useCrmRailItems(items: readonly RailItem[], enabled: boolean): RailItem[] {
  const { map: crmPerms, loaded } = useCrmPermissions(enabled);
  const activeObjects = useActiveCrmObjectNames(enabled);
  const customObjects = useCustomCrmObjects(enabled);

  return useMemo(() => {
    if (!enabled) return [...items];

    const kept = items.filter((it) => {
      const apiName = CRM_ENTITY_RAIL_OBJECTS[it.id];
      // Fail-open: only hide once we know the object set and it excludes this one.
      return !apiName || !activeObjects || activeObjects.has(apiName);
    });

    const dynamic: RailItem[] = customObjects
      .filter((o) =>
        crmRowVisible(
          {
            // Generic "see custom objects" switch (seeded key granted to the
            // system templates), refined by the object's own view key.
            permKey: 'crm.customobject.read',
            objectApiName: o.api_name,
            objectPermKey: `module.${o.api_name}.view`,
          },
          crmPerms,
          activeObjects,
          loaded,
        ),
      )
      .map((o) => ({
        id: `crm-object-${o.api_name}`,
        label: o.label,
        icon: resolveObjectIcon(o.icon, o.api_name),
        href: `/crm/o/${o.api_name}`,
      }));

    if (dynamic.length === 0) return kept;

    // Insert after the last built-in entity icon (Tasks, unless deactivated), so
    // custom objects never land above Home or below Operations.
    let insertAt = kept.length;
    for (let i = kept.length - 1; i >= 0; i--) {
      if (CRM_ENTITY_RAIL_OBJECTS[kept[i]!.id]) {
        insertAt = i + 1;
        break;
      }
    }
    return [...kept.slice(0, insertAt), ...dynamic, ...kept.slice(insertAt)];
  }, [enabled, items, customObjects, activeObjects, crmPerms, loaded]);
}


export const SYSTEM_OBJECT_ICONS: Record<string, LucideIcon> = {
  company: Building2,
  contact: Contact,
  lead: Target,
  deal: Handshake,
  task: ClipboardList,
  product: Package,
  package: Layers,
  subscription: RefreshCw,
};

/** Resolve an object's icon — the shell-side twin of the CRM app's
 *  resolveObjectIcon, since the shell can't import CRM internals. Precedence:
 *  the schema's own stored Lucide icon name, then the system-object default for
 *  its `api_name`, then `Boxes` so a row never renders a broken tile. */
export function resolveObjectIcon(name: string | null, apiName?: string): LucideIcon {
  if (name) {
    const found = (LucideIcons as unknown as Record<string, LucideIcon>)[name];
    if (found) return found;
  }
  if (apiName && SYSTEM_OBJECT_ICONS[apiName]) return SYSTEM_OBJECT_ICONS[apiName];
  return Boxes;
}

/* ── Payroll (clario-payroll AppShell secondary-panel parity) ──
   The source payroll app rendered a two-part sidebar (icon rail + secondary
   panel) from its AppShell `NAV_ITEMS`. In the ecosystem the layout owns the
   panel chrome and the shell supplies the per-section body — this mirrors that
   NAV_ITEMS sub-page map (hrefs remapped under /payroll). A link with a
   `permission` slug is filtered by the payroll matrix once the remote has
   published it (loadedApps.payroll); the destination page still enforces its
   own permission server-side. */

